/**
 * Scene Editor properties inspector — PlayEngine FieldRow chrome.
 * Same gameConfig paths as classic Version Settings; prototyper-style controls.
 */

import React, { useCallback, useMemo } from "react";
import _ from "lodash";
import { Typography } from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import {
  buildEffectiveGameConfig,
  rewriteComponentPathForLocalizationOverride,
  shouldUseLocalizationOverride,
} from "../../modules/localizationOverrides";
import { SECTION_DIVIDER, BAR_BG, PANEL_BG, SectionHeader } from "./sceneEditorInspector";
import SceneEditorSettingsList from "./sceneEditorSettingField";
import { parseSceneEditorHierarchySelection } from "./sceneEditorHierarchySelection";
import { relocatedOptionSettingsForComponent } from "../../modules/sceneEditorRelocatedOptions";

const useStyles = makeStyles({
  card: { margin: "5px" },
  imageSelector: { width: "100%" },
  imageSetSelector: { width: "100%" },
  cardOrderSelector: { width: "100%" },
  sliderSize: { width: "100%" },
  editorItem: { width: "100%" },
});

/**
 * @param {object} props
 * @param {string[]} props.selectedComponentIds
 */
export default function containerSceneEditorInspector(props) {
  const classes = useStyles();
  const selectedComponentIds = (props.selectedComponentIds || []).filter(
    (id) => id && !String(id).startsWith("cat-")
  );

  const realSelectedVersion = props.selectedVersion;
  const realSetSelectedVersionPathValue = props.setSelectedVersionPathValue;
  const isLocalization = props.settingsSource === "localization";
  const selectedLanguage =
    props.selectedLanguage || _.get(realSelectedVersion, "gameConfig.options.language", "en");

  const selectedVersionForEditors = useMemo(() => {
    if (isLocalization) return realSelectedVersion;
    const gameConfig = _.get(realSelectedVersion, "gameConfig");
    if (!shouldUseLocalizationOverride(gameConfig, selectedLanguage)) {
      return realSelectedVersion;
    }
    return {
      ...realSelectedVersion,
      gameConfig: buildEffectiveGameConfig(gameConfig, selectedLanguage),
    };
  }, [isLocalization, realSelectedVersion, selectedLanguage]);

  const setSelectedVersionPathValue = useCallback(
    (p, disableUndo) => {
      if (isLocalization) {
        realSetSelectedVersionPathValue(p, disableUndo);
        return;
      }
      realSetSelectedVersionPathValue(
        rewriteComponentPathForLocalizationOverride(
          p,
          _.get(realSelectedVersion, "gameConfig"),
          selectedLanguage
        ),
        disableUndo
      );
    },
    [isLocalization, realSelectedVersion, realSetSelectedVersionPathValue, selectedLanguage]
  );

  const getSettingPath = useCallback(
    (component, setting) => {
      const id = component.gameComponentId || component.id;
      return `gameConfig.components.${id}.localization.${selectedLanguage}.${setting.path}`;
    },
    [selectedLanguage]
  );

  const editorProps = useMemo(
    () => ({
      ...props,
      selectedVersion: selectedVersionForEditors,
      setSelectedVersionPathValue,
      classes: { ...classes, ...(props.classes || {}) },
    }),
    [props, selectedVersionForEditors, setSelectedVersionPathValue, classes]
  );

  const setValue = (p, disableUndo = false) => {
    setSelectedVersionPathValue(p, disableUndo);
  };

  const settingsComponents = _.get(
    props,
    isLocalization
      ? "selectedModifiedBase.localizationSettings.components"
      : "selectedModifiedBase.versionSettings.components",
    []
  );

  const selectedId = selectedComponentIds[0];
  const selection = parseSceneEditorHierarchySelection(selectedId);

  const selectedBundle = useMemo(() => {
    if (!selection) return null;
    const related = settingsComponents.filter((c) => c.id === selection.componentId);
    if (related.length === 0) {
      return { kind: "missing", component: { id: selection.componentId, caption: selection.componentId, settings: [] }, parent: null };
    }

    const main =
      related.find((c) => !c.isGroup && !c.isSubGroup && _.isNil(c.levelIndex)) ||
      related.find((c) => !c.isGroup && !c.isSubGroup) ||
      related[0];

    if (selection.type === "group") {
      const group = related.find((c) => c.isGroup && c.caption === selection.groupCaption);
      if (!group) return { kind: "missing", component: main, parent: null };
      return { kind: "group", component: group, parent: main };
    }

    if (selection.type === "subgroup") {
      const subGroup = related.find(
        (c) =>
          c.isSubGroup &&
          c.caption === selection.subGroupCaption &&
          c.parentGroup === selection.groupCaption
      );
      if (!subGroup) return { kind: "missing", component: main, parent: null };
      return { kind: "subgroup", component: subGroup, parent: main };
    }

    return { kind: "component", component: main, parent: null };
  }, [selection, settingsComponents]);

  if (
    _.get(props, "areMediaLoaded") !== true ||
    _.get(props, "areAtlasesLoaded") !== true ||
    _.get(props, "areSpinesLoaded") !== true ||
    _.get(props, "areSoundsLoaded") !== true ||
    _.get(props, "areFontsLoaded") !== true
  ) {
    return (
      <div style={{ padding: 16 }}>
        <Typography variant="body2" style={{ color: "#888" }}>
          Loading media…
        </Typography>
      </div>
    );
  }

  if (selectedComponentIds.length === 0 || !selectedBundle) {
    return (
      <div style={{ padding: "20px 16px" }}>
        <Typography variant="body2" style={{ color: "#888", fontSize: 13 }}>
          {isLocalization
            ? "Select a component to edit its localization."
            : "Select a component to edit its properties."}
        </Typography>
      </div>
    );
  }

  if (selectedComponentIds.length > 1) {
    return (
      <div style={{ padding: "20px 16px" }}>
        <Typography variant="body2" style={{ color: "#888", fontSize: 13 }}>
          {selectedComponentIds.length} components selected — pick one to edit.
        </Typography>
      </div>
    );
  }

  const { kind, component } = selectedBundle;
  const headerTitle = component.caption || component.id;
  const editorClasses = editorProps.classes;

  function renderEditors(comp) {
    const extras =
      !isLocalization && kind === "component"
        ? relocatedOptionSettingsForComponent(comp, realSelectedVersion)
        : [];
    const merged =
      extras.length > 0 ? { ...comp, settings: [...extras, ...(comp.settings || [])] } : comp;
    if (!merged || !_.get(merged, "settings.length")) {
      return (
        <Typography variant="body2" style={{ color: "#888", fontSize: 13, padding: "8px 8px 0" }}>
          {isLocalization
            ? "This component has no localization settings."
            : "This component has no settings."}
        </Typography>
      );
    }
    return (
      <SceneEditorSettingsList
        component={merged}
        props={editorProps}
        classes={editorClasses}
        setValue={setValue}
        realSelectedVersion={realSelectedVersion}
        getSettingPath={isLocalization ? getSettingPath : undefined}
      />
    );
  }

  return (
    <div className="scene-editor-props-panel proto-props-panel" style={{ backgroundColor: PANEL_BG }}>
      <style>{`
        .scene-editor-props-panel .MuiInputBase-root {
          background-color: #212226;
          border: 1px solid #3a3a3f;
          border-radius: 4px;
          min-height: 24px;
        }
        .scene-editor-props-panel .MuiInputBase-root.Mui-focused {
          border-color: #64b5f6;
        }
        .scene-editor-props-panel .MuiInputBase-input {
          padding: 3px 6px;
          font-size: 12px;
        }
        .scene-editor-props-panel .scene-editor-color-swatch {
          -webkit-appearance: none;
          appearance: none;
        }
        .scene-editor-props-panel .scene-editor-color-swatch::-webkit-color-swatch-wrapper {
          padding: 0;
        }
        .scene-editor-props-panel .scene-editor-color-swatch::-webkit-color-swatch {
          border: none;
          border-radius: 3px;
        }
        .scene-editor-props-panel .scene-editor-color-swatch::-moz-color-swatch {
          border: none;
          border-radius: 3px;
        }
        .scene-editor-props-panel .MuiInput-underline:before,
        .scene-editor-props-panel .MuiInput-underline:after {
          display: none;
        }
        .scene-editor-props-panel .MuiSelect-select {
          padding: 3px 24px 3px 6px;
          background: transparent;
        }
        .scene-editor-props-panel .MuiSwitch-root {
          margin: 0;
        }
        .scene-editor-classic-fallback {
          width: 100%;
          max-width: 100%;
        }
        /* Hide classic editor captions — FieldRow supplies the label (per-row and batch wrapSetting).
           Keep Typography used as Select closed values. */
        .scene-editor-classic-fallback .MuiTypography-root,
        .scene-editor-classic-fallback .MuiFormControlLabel-label {
          display: none !important;
        }
        .scene-editor-classic-fallback .MuiSelect-select .MuiTypography-root {
          display: block !important;
          /* Don't zero margin-left — selectors put space between thumb and filename. */
          margin-top: 0 !important;
          margin-bottom: 0 !important;
          margin-right: 0 !important;
          color: #e8e8e8;
          font-size: 12px;
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
        }
        .scene-editor-classic-fallback .MuiFormControlLabel-root {
          margin: 0;
        }
        .scene-editor-classic-fallback .MuiSelect-root {
          flex: 1 1 auto;
          min-width: 0;
          max-width: 100%;
        }
        .scene-editor-classic-fallback > div,
        .scene-editor-classic-control > div {
          width: 100%;
          max-width: 100%;
          margin: 0;
        }
        .scene-editor-classic-control {
          width: 100%;
          max-width: 100%;
          min-width: 0;
        }
      `}</style>

      <div
        style={{
          position: "sticky",
          top: 0,
          zIndex: 2,
          display: "flex",
          alignItems: "baseline",
          gap: 8,
          padding: "10px 12px",
          backgroundColor: BAR_BG,
          borderBottom: `1px solid ${SECTION_DIVIDER}`,
        }}
      >
        <span style={{ fontSize: 13, fontWeight: 600, color: "#e8e8e8" }}>
          {headerTitle}
        </span>
      </div>

      <SectionHeader title={isLocalization ? "Localization" : "Properties"} />
      <div style={{ padding: "2px 4px 16px" }}>{renderEditors(component)}</div>
    </div>
  );
}
